import { useEffect, useRef, lazy, Suspense } from 'react'
import { ChannelPlayer } from './ChannelPlayer'
import { SchedulePlayer } from './SchedulePlayer'
import { PlaylistPlayer } from './PlaylistPlayer'
const CanvasLayoutPlayer = lazy(() => import('./CanvasLayoutPlayer').then((m) => ({ default: m.CanvasLayoutPlayer })))
import { EmptyContent } from './EmptyContent'
import { Screen, ScreenType, ChurnEvents } from 'common/types'
import { OverlayInfoBanner } from './OverlayInfo'
import { BasicPlanWatermark } from './BasicPlanWatermark'
import { debugOverlay } from './debugOverlay'
import { GatedDefault } from './GatedDefault'
import { track } from '../../analytics'

interface SourcePlayerProps {
  screen: Screen
}

const isPremiumFeatureOnBasicScreen = (screen: Screen): boolean => {
  return screen.screenType === ScreenType.BASIC && (!!screen.channel || !!screen.canvasLayout || !!screen.schedule)
}

const Player = ({ screen }: SourcePlayerProps) => {
  return screen?.schedule ? (
    <SchedulePlayer scheduleId={screen.schedule.id} screenTimezone={screen.timezone} />
  ) : screen?.canvasLayout ? (
    <Suspense fallback={null}>
      <CanvasLayoutPlayer layoutId={screen.canvasLayout.id} />
    </Suspense>
  ) : screen?.channel ? (
    <ChannelPlayer channelId={screen.channel.id} />
  ) : screen?.playlist ? (
    <PlaylistPlayer playlistId={screen.playlist.id} />
  ) : (
    <EmptyContent />
  )
}

export const SourcePlayer = ({ screen }: SourcePlayerProps) => {
  const hasSentPremiumBannerEvent = useRef(false)
  const hasSentGatedScreenEvent = useRef(false)

  const showPremiumBanner = isPremiumFeatureOnBasicScreen(screen)
  const isGatedScreen = screen.screenType === ScreenType.GATED
  const showBasicWatermark = screen.screenType === ScreenType.BASIC && !isGatedScreen

  useEffect(() => {
    if (screen.localPlayback) {
      debugOverlay.set('local', 'Local: ON')
    } else {
      debugOverlay.remove('local')
    }
  }, [screen.localPlayback])

  useEffect(() => {
    if (showPremiumBanner && !hasSentPremiumBannerEvent.current) {
      track(ChurnEvents.PREMIUM_BANNER_SHOWN, {
        screen_id: screen.id,
        screen_name: screen.name,
        screen_type: screen.screenType,
        channel_id: screen.channel?.id,
        channel_name: screen.channel?.name,
      })
      hasSentPremiumBannerEvent.current = true
    }
  }, [showPremiumBanner, screen])

  useEffect(() => {
    if (isGatedScreen && !hasSentGatedScreenEvent.current) {
      track(ChurnEvents.GATED_SCREEN_SHOWN, {
        screen_id: screen.id,
        screen_name: screen.name,
      })
      hasSentGatedScreenEvent.current = true
    }
  }, [isGatedScreen, screen])

  return (
    <>
      {showPremiumBanner && (
        <OverlayInfoBanner
          text="Channels, Scheduling & Layouts need a premium screen. Upgrade this screen or use Playlists for basic content display."
          moveIntervalInSeconds={2}
          logoUrl="signagespace.png"
        />
      )}
      {isGatedScreen ? <GatedDefault /> : <Player screen={screen} />}
      {showBasicWatermark && <BasicPlanWatermark />}
    </>
  )
}
